import cn from 'classnames';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useState } from 'react';

import { getCorrectPathname } from '@utils/getCorrectPathname';
import { buildPaginationQuery } from '@utils/paginationUrl';

import Button from '@components/uiKit/Button';

import ChevronLeft from '@icons/24/pagination-arrow-left.svg';
import ChevronRight from '@icons/24/pagination-arrow-right.svg';

import { buildMobilePaginationItems, buildPaginationItems } from './buildPaginationItems';
import styles from './Pagination.module.scss';
import { PaginationProps } from './types';

const MOBILE_MEDIA_QUERY = '(max-width: 767px)';

const useIsMobileViewport = () => {
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
            return;
        }

        const mediaQuery = window.matchMedia(MOBILE_MEDIA_QUERY);
        const update = () => setIsMobile(mediaQuery.matches);

        update();
        mediaQuery.addEventListener('change', update);

        return () => mediaQuery.removeEventListener('change', update);
    }, []);

    return isMobile;
};

const scrollToTopCustom = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
};

export const Pagination = ({
    pages,
    baseNumberPages = 9,
    scrollToTop = false,
    className,
    isLoadMoreOnly = false,
    isLoadMoreAvailable = true,
    isVisibleShowMore = false,
    currentPage,
}: PaginationProps) => {
    const { query, asPath } = useRouter();

    const page = +(query.page || currentPage || 1);

    const getParams = (newPage: number, isLoadMore = false) => ({
        pathname: getCorrectPathname(asPath),
        query: buildPaginationQuery(query, newPage, { isLoadMore }),
    });

    const isMobileViewport = useIsMobileViewport();

    const paginationArray = useMemo(
        () =>
            isMobileViewport
                ? buildMobilePaginationItems(page, pages)
                : buildPaginationItems(page, pages, baseNumberPages),
        [baseNumberPages, isMobileViewport, page, pages]
    );

    const controlClassName = cn(styles.pagination__control, isLoadMoreOnly && styles.pagination__control_loadMoreOnly);

    const itemClassName = cn(styles.pagination__item, isLoadMoreOnly && styles.pagination__item_loadMoreOnly);

    return (
        <div className={cn(styles.pagination, isMobileViewport && styles.pagination_mobile)}>
            {page !== pages && isVisibleShowMore && (
                <div className={styles.pagination__loadMoreWrap}>
                    <Link
                        legacyBehavior
                        href={getParams(page + 1, true)}
                        passHref
                        prefetch={false}
                        scroll={scrollToTop}
                    >
                        <Button
                            size="lg"
                            data-testid="button-expand"
                            variant="reserveMono"
                            onClick={() => getParams(page + 1)}
                            className={cn(
                                styles.pagination__loadMoreButton,
                                !isLoadMoreAvailable && styles.pagination__loadMoreButton_hidden
                            )}
                        >
                            Загрузить еще
                        </Button>
                    </Link>
                </div>
            )}

            <ul className={cn(styles.pagination__list, className)}>
                {page === 1 ? null : (
                    <li
                        className={itemClassName}
                        onClick={() => {
                            scrollToTopCustom();
                        }}
                    >
                        <Link legacyBehavior href={getParams(page - 1)} passHref prefetch={false}>
                            <a aria-label="влево" className={cn(controlClassName, styles.pagination__control_arrow)}>
                                <ChevronLeft />
                            </a>
                        </Link>
                    </li>
                )}

                {!isLoadMoreOnly &&
                    paginationArray.map((item, index) => (
                        <li
                            key={index}
                            className={itemClassName}
                            onClick={() => {
                                scrollToTopCustom();
                            }}
                        >
                            {item ? (
                                item === page ? (
                                    <span className={cn(controlClassName, styles.pagination__control_active)}>
                                        {item}
                                    </span>
                                ) : (
                                    <Link legacyBehavior href={getParams(item)} passHref prefetch={false}>
                                        <a aria-label="Ссылка на страницу" className={controlClassName}>
                                            {item}
                                        </a>
                                    </Link>
                                )
                            ) : (
                                <span className={styles.pagination__ellipsis}>...</span>
                            )}
                        </li>
                    ))}

                {page === pages ? null : (
                    <li
                        className={itemClassName}
                        onClick={() => {
                            page !== pages && scrollToTopCustom();
                        }}
                    >
                        <Link legacyBehavior href={getParams(page + 1)} passHref prefetch={false}>
                            <a
                                aria-label="вправо"
                                className={cn(
                                    controlClassName,
                                    styles.pagination__control_arrow,
                                    !isLoadMoreAvailable && styles.pagination__control_hidden
                                )}
                            >
                                <ChevronRight />
                            </a>
                        </Link>
                    </li>
                )}
            </ul>
        </div>
    );
};

export default Pagination;
